/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { EntityType } from 'src/graphql/generated/graphql.generated';
import {
  WebOrderLogEntityChangesRecord,
  WebOrderLogEntityChangesProps,
} from 'src/store/generated/web-order-log-entity-changes-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface WebOrderLogProps {
  id: string;
  requestJson: string;
  name: string;
  isSuccess: boolean;
  errors: string | null;
  entityId: string | null;
  entityType: EntityType | null;
  changes: List<WebOrderLogEntityChangesProps> | null;
  dateAcknowledged: Date | null;
  acknowledgedById: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class WebOrderLogRecord extends Record<WebOrderLogProps>({
  id: null as any,
  requestJson: null as any,
  name: null as any,
  isSuccess: null as any,
  errors: null as any,
  entityId: null as any,
  entityType: null as any,
  changes: null as any,
  dateAcknowledged: null as any,
  acknowledgedById: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    requestJson: string;
    name: string;
    isSuccess: boolean;
    errors: string | null;
    entityId: string | null;
    entityType: EntityType | null;
    changes: any;
    dateAcknowledged: string | number | Date | null;
    acknowledgedById: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): WebOrderLogRecord {
    if (json == null) {
      throw new Error('WebOrderLogRecord.fromJS: parameter cannot be null');
    }
    return new WebOrderLogRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'WebOrderLogJson.id',
      ),
      requestJson: getFromOrThrow(json, (j) => j.requestJson, 'WebOrderLogJson.requestJson '),
      name: getFromOrThrow(json, (j) => j.name, 'WebOrderLogJson.name'),
      isSuccess: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isSuccess', false),
        'WebOrderLogJson.isSuccess',
      ),
      errors: json.errors,
      entityId: json.entityId,
      entityType: json.entityType,
      changes:
        json.changes != null
          ? List<WebOrderLogEntityChangesRecord>(
              json.changes.map((o: any) => new WebOrderLogEntityChangesRecord(o)),
            )
          : null,
      dateAcknowledged: json.dateAcknowledged != null ? new Date(json.dateAcknowledged) : null,
      acknowledgedById: json.acknowledgedById,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    requestJson: string;
    name: string;
    isSuccess: boolean;
    errors: string | null;
    entityId: string | null;
    entityType: EntityType | null;
    changes: any;
    dateAcknowledged: string | number | Date | null;
    acknowledgedById: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [WebOrderLogRecord, Error[]] {
    if (json == null) {
      throw new Error('WebOrderLogRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new WebOrderLogRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'WebOrderLogJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestJson: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.requestJson, 'WebOrderLogJson.requestJson '),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'WebOrderLogJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isSuccess: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isSuccess', false),
            'WebOrderLogJson.isSuccess',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      errors: tryOrDefault(
        () => json.errors,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      entityId: tryOrDefault(
        () => json.entityId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      entityType: tryOrDefault(
        () => json.entityType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      changes: tryOrDefault(
        () =>
          json.changes != null
            ? List<WebOrderLogEntityChangesRecord>(
                json.changes.map((o: any) => new WebOrderLogEntityChangesRecord(o)),
              )
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateAcknowledged: tryOrDefault(
        () => (json.dateAcknowledged != null ? new Date(json.dateAcknowledged) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      acknowledgedById: tryOrDefault(
        () => json.acknowledgedById,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: WebOrderLogProps) {
    super(props);
    if (props == null) {
      throw new Error('WebOrderLogRecord constructor parameter cannot be null');
    }
  }
}

export abstract class WebOrderLogViewModelBase implements WebOrderLogProps {
  protected readonly _props: WebOrderLogProps;
  private _json?: WebOrderLogProps;

  get id(): string {
    return this._props.id;
  }
  get requestJson(): string {
    return this._props.requestJson;
  }
  get name(): string {
    return this._props.name;
  }
  get isSuccess(): boolean {
    return this._props.isSuccess;
  }
  get errors(): string | null {
    return this._props.errors;
  }
  get entityId(): string | null {
    return this._props.entityId;
  }
  get entityType(): EntityType | null {
    return this._props.entityType;
  }
  get changes(): List<WebOrderLogEntityChangesProps> | null {
    return this._props.changes;
  }
  get dateAcknowledged(): Date | null {
    return this._props.dateAcknowledged;
  }
  get acknowledgedById(): string | null {
    return this._props.acknowledgedById;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAirport() {
    return this._props.entityType === EntityType.Airport;
  }
  public get isBase() {
    return this._props.entityType === EntityType.Base;
  }
  public get isCard() {
    return this._props.entityType === EntityType.Card;
  }
  public get isCommunicationCenter() {
    return this._props.entityType === EntityType.CommunicationCenter;
  }
  public get isCompany() {
    return this._props.entityType === EntityType.Company;
  }
  public get isControlZone() {
    return this._props.entityType === EntityType.ControlZone;
  }
  public get isLocation() {
    return this._props.entityType === EntityType.Location;
  }
  public get isMedicalFacility() {
    return this._props.entityType === EntityType.MedicalFacility;
  }
  public get isOutOfServiceEvent() {
    return this._props.entityType === EntityType.OutOfServiceEvent;
  }
  public get isPaipEvent() {
    return this._props.entityType === EntityType.PaipEvent;
  }
  public get isPerson() {
    return this._props.entityType === EntityType.Person;
  }
  public get isProgram() {
    return this._props.entityType === EntityType.Program;
  }
  public get isRegion() {
    return this._props.entityType === EntityType.Region;
  }
  public get isTimer() {
    return this._props.entityType === EntityType.Timer;
  }
  public get isUnit() {
    return this._props.entityType === EntityType.Unit;
  }
  public get isUnspecified() {
    return this._props.entityType === EntityType.Unspecified;
  }
  public get isVehicle() {
    return this._props.entityType === EntityType.Vehicle;
  }

  public isAnyEntityType(...entityType: EntityType[]) {
    return this._props.entityType != null && entityType.includes(this._props.entityType);
  }

  constructor(props: WebOrderLogProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        requestJson: this._props.requestJson,
        name: this._props.name,
        isSuccess: this._props.isSuccess,
        errors: this._props.errors,
        entityId: this._props.entityId,
        entityType: this._props.entityType,
        changes: this._props.changes,
        dateAcknowledged: this._props.dateAcknowledged,
        acknowledgedById: this._props.acknowledgedById,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllWebOrderLogEntities = (state: AppState) =>
  state.entityStores.webOrderLog.entities;

export const selectWebOrderLogById = (id: string) => (state: AppState) =>
  state.entityStores.webOrderLog.entities.get(id);

export const selectWebOrderLogMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.webOrderLog.meta.get(id);

export const selectWebOrderLogAndMetadataById = (id: string) =>
  createSelector(
    selectWebOrderLogById(id),
    selectWebOrderLogMetadataById(id),
    (webOrderLog, meta) => ({ webOrderLog, meta }),
  );

export function selectWebOrderLogEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllWebOrderLogEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, WebOrderLogRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectWebOrderLogEntitiesByRequestJson(requestJson: string) {
  return createSelector(selectAllWebOrderLogEntities, (entities) =>
    entities.filter((e) => e.requestJson === requestJson).valueSeq(),
  );
}

export function selectWebOrderLogEntitiesByName(name: string) {
  return createSelector(selectAllWebOrderLogEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectWebOrderLogEntitiesByIsSuccess(isSuccess: boolean) {
  return createSelector(selectAllWebOrderLogEntities, (entities) =>
    entities.filter((e) => e.isSuccess === isSuccess).valueSeq(),
  );
}

export function selectWebOrderLogEntitiesByErrors(errors: string) {
  return createSelector(selectAllWebOrderLogEntities, (entities) =>
    entities.filter((e) => e.errors === errors).valueSeq(),
  );
}

export function selectWebOrderLogEntitiesByEntityId(entityId: string) {
  return createSelector(selectAllWebOrderLogEntities, (entities) =>
    entities.filter((e) => e.entityId === entityId).valueSeq(),
  );
}

export function selectWebOrderLogEntitiesByEntityType(entityType: EntityType) {
  return createSelector(selectAllWebOrderLogEntities, (entities) =>
    entities.filter((e) => e.entityType === entityType).valueSeq(),
  );
}

export function selectWebOrderLogEntitiesByDateAcknowledged(dateAcknowledged: Date) {
  return createSelector(selectAllWebOrderLogEntities, (entities) =>
    entities.filter((e) => e.dateAcknowledged === dateAcknowledged).valueSeq(),
  );
}

export function selectWebOrderLogEntitiesByAcknowledgedById(acknowledgedById: string) {
  return createSelector(selectAllWebOrderLogEntities, (entities) =>
    entities.filter((e) => e.acknowledgedById === acknowledgedById).valueSeq(),
  );
}
//#endregion Selectors
